import React from 'react';
import { Formik, Form } from 'formik';
import Button from '../../components/button';
import InputText from '../../components/input-text/inputText';

const SellSharesInput = ({
  itemId,
  value,
  max,
  error,
  onCommit,
}: {
  itemId: string;
  value: number;
  max: number;
  error?: string;
  onCommit: (itemId: string, field: string, value: number) => void;
}) => {
  return (
    <Formik initialValues={{ sellShares: value || '' }} enableReinitialize onSubmit={() => {}}>
      {({ setFieldValue }) => (
        <Form>
          <div style={{ padding: '16px 0px 0px 0px ' }}>
            <InputText
              name='sellShares'
              type='number'
              placeholder=''
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('sellShares', e.target.value);
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                onCommit(itemId, 'sellShares', parseFloat(e.target.value) || 0);
              }}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              width='80px'
              errorValue={!!error}
              touchedValue={!!error}
              className='sell-shares-input'
            />
          </div>
          {!!error && (
            <div className='field-error-message' style={{ fontSize: '11px', marginTop: '2px' }}>
              {error}
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

interface DoneTradeSellItemsHeaderProps {
  actions: Record<string, any>[];
  onEnterFill: (row: any) => void;
  onUpdateRow: (itemId: string, field: string, value: string | number) => void;
  submittingItemId: string | null;
  canFillDT: boolean;
}

interface CellProps {
  row: any;
  cell: any;
}

const DoneTradeSellItemsHeader = ({
  actions,
  onEnterFill,
  onUpdateRow,
  submittingItemId,
  canFillDT,
}: DoneTradeSellItemsHeaderProps) => {
  const renderSellSharesCell = ({ row, cell }: CellProps) => {
    const original = row?.original;
    return (
      <td style={{ minWidth: cell.column.minWidth, width: cell.column.width, textAlign: 'center' }}>
        <SellSharesInput
          itemId={original.itemId}
          value={original.sellShares || 0}
          max={original.maxShares}
          error={original.rowError}
          onCommit={onUpdateRow}
        />
      </td>
    );
  };

  const renderBookingTypeCell = ({ row, cell }: CellProps) => {
    return (
      <td style={{ minWidth: cell.column.minWidth, width: cell.column.width, textAlign: 'center' }}>
        <span style={{ fontSize: '14px' }}>Done Trade</span>
      </td>
    );
  };

  const renderActionCell = ({ row, cell }: CellProps) => {
    const original = row?.original;
    const isThisRowSubmitting = submittingItemId === original?.itemId;
    const isAnySubmitting = submittingItemId !== null;
    const disabled = isAnySubmitting || !canFillDT;

    return (
      <td
        style={{
          minWidth: cell.column.minWidth,
          width: cell.column.width,
          textAlign: 'center',
        }}>
        <span title='Enter Fill'>
          <Button
            variant='secondary'
            title='Enter Fill'
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onEnterFill(original);
            }}
            disabled={disabled}
            loading={isThisRowSubmitting}
          />
        </span>
      </td>
    );
  };

  const columnsWithOverrides = (actions || []).map((col: any) => {
    if (col.accessor === 'sellShares') {
      return { ...col, renderCell: true, Cell: renderSellSharesCell };
    }
    if (col.accessor === 'bookingType') {
      return { ...col, width: 120, minWidth: 120, renderCell: true, Cell: renderBookingTypeCell };
    }
    return col;
  });

  return [
    ...columnsWithOverrides,
    {
      Header: 'Action',
      accessor: 'doneTradeSellAction',
      width: 120,
      minWidth: 110,
      sortable: false,
      renderCell: true,
      center: true,
      Cell: renderActionCell,
    },
  ];
};

export default DoneTradeSellItemsHeader;
